import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "./firebase";

import LottieSmart from "./components/animation/LottieSmart.jsx";

export default function Bootypage() {
  const navigate = useNavigate();
  const location = useLocation();

  // ------------------------------------
  // STATE
  // ------------------------------------
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSignUp, setIsSignUp] = useState(true);
  const [error, setError] = useState(null);

  // ------------------------------------
  // COURSE-BASED MESSAGING
  // ------------------------------------
  const params = new URLSearchParams(location.search);
  const course = params.get("course");

  const messaging = {
    propfunding: {
      title: "AI-Powered Prop Funding Training",
      subtitle: "Real-world learning that helps you pass funded challenges.",
    },
    trading: {
      title: "AI-Powered Trading Training",
      subtitle: "Learn trading with real charts, step by step.",
    },
    investing: {
      title: "AI-Powered Investing Training",
      subtitle: "Learn long-term wealth building the smart way.",
    },
  };

  const text = messaging[course] || messaging.trading;

  // ------------------------------------
  // SECURE ERROR MAPPING (NO FIREBASE MESSAGES)
  // ------------------------------------
  const mapAuthError = (firebaseError) => {
    const code = firebaseError.code;

    // Email issues
    if (code === "auth/invalid-email") return "Please enter a valid email address.";
    if (code === "auth/user-not-found") return "No account found with that email.";
    if (code === "auth/email-already-in-use")
      return "An account with this email already exists.";

    // Password issues
    if (code === "auth/wrong-password")
      return "Incorrect password. Please try again.";
    if (code === "auth/weak-password")
      return "Your password must be at least 8 characters long.";

    // Missing fields
    if (code === "auth/missing-password")
      return "Please enter your password.";
    if (code === "auth/missing-email")
      return "Please enter your email address.";

    // Rate limit / too many attempts
    if (code === "auth/too-many-requests")
      return "Too many attempts. Please try again later.";

    // Default (no internal info leaked)
    return "Something went wrong. Please try again.";
  };

  // ------------------------------------
  // AUTH HANDLERS
  // ------------------------------------
  const handleSignUp = async () => {
    try {
      setError(null);
      await createUserWithEmailAndPassword(auth, email, password);
      navigate("/welcome-login");
    } catch (err) {
      setError(mapAuthError(err));
    }
  };

  const handleLogin = async () => {
    try {
      setError(null);
      await signInWithEmailAndPassword(auth, email, password);
      navigate("/welcome-login");
    } catch (err) {
      setError(mapAuthError(err));
    }
  };

  const toggleMode = () => {
    setIsSignUp((prev) => !prev);
    setError(null);
  };

  // ------------------------------------
  // RENDER UI
  // ------------------------------------
  return (
    <>
      <style>{`
        * {
          font-family: "Nunito Sans", sans-serif !important;
        }

        html, body {
          margin: 0;
          padding: 0;
          height: 100%;
          overflow: hidden !important;
          background: #121416;
        }

        .ft-login-wrapper {
          width: 100vw;
          height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background: #121416;
        }

        .ft-login-card {
          width: 420px;
          background: #ffffff;
          border-radius: 22px;
          padding: 45px 35px;
          box-shadow: 0px 10px 22px rgba(0,0,0,0.35);
          text-align: center;
          max-height: 92vh;
          overflow: hidden;
        }

        .ft-title {
          font-size: 24px;
          font-weight: 800;
          color: #232830;
          margin-bottom: 10px;
        }

        .ft-subtitle {
          font-size: 15px;
          color: #555;
          background: #FFF2E8;
          padding: 10px 14px;
          border-radius: 10px;
          margin-bottom: 20px;
        }

        .ft-animation-box {
          margin: 0 auto 22px auto;
          display: flex;
          justify-content: center;
        }

        .ft-label {
          display: block;
          text-align: left;
          font-size: 14px;
          font-weight: 700;
          color: #232830;
          margin-top: 14px;
        }

        .ft-input {
          width: 100%;
          padding: 12px 14px;
          font-size: 15px;
          background: #F3F4F6;
          border: 1px solid #DDD;
          border-radius: 10px;
          margin-top: 6px;
          outline: none;
        }

        .ft-input:focus {
          border-color: #FF7C24;
        }

        .ft-small-note {
          font-size: 12px;
          color: #777;
          text-align: left;
          margin-top: 4px;
        }

        .ft-error {
          color: #d11;
          margin-top: 12px;
          font-size: 14px;
          font-weight: 600;
        }

        .ft-primary-btn {
          width: 100%;
          margin-top: 16px;
          background: #FF7C24;
          color: #fff;
          padding: 14px;
          border-radius: 12px;
          border: none;
          font-size: 17px;
          font-weight: 700;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .ft-primary-btn:hover {
          opacity: 0.85;
        }

        .ft-toggle {
          margin-top: 12px;
          font-size: 14px;
          color: #444;
        }

        .ft-link {
          color: #FF7C24;
          cursor: pointer;
          font-weight: 700;
        }

        .ft-footer-links {
          margin-top: 18px;
          font-size: 12px;
          color: #666;
        }
      `}</style>

      <div className="ft-login-wrapper">
        <div className="ft-login-card">

          {/* Animation */}
          <div className="ft-animation-box">
            <div
              style={{
                width: 140,
                height: 140,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <LottieSmart
                src="/assets/animations/coin_sparkle.json"
                fallbackSrc="https://lottie.host/17f89781-7c01-4038-b8eb-34f05839ad8e/I3He0k0iNH.json"
                style={{ width: 140, height: 140 }}
                loop
                autoplay
              />
            </div>
          </div>

          {/* Dynamic Title */}
          <h1 className="ft-title">{text.title}</h1>
          <p className="ft-subtitle">{text.subtitle}</p>

          {/* Email */}
          <label className="ft-label">Email address</label>
          <input
            type="email"
            className="ft-input"
            placeholder="Enter your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          {/* Password */}
          <label className="ft-label">Password</label>
          <input
            type="password"
            className="ft-input"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <p className="ft-small-note">Password must be at least 8 characters long</p>

          {/* Error */}
          {error && <p className="ft-error">{error}</p>}

          {/* CTA */}
          <button
            className="ft-primary-btn"
            onClick={isSignUp ? handleSignUp : handleLogin}
          >
            {isSignUp ? "Sign Up" : "Login"}
          </button>

          {/* Toggle */}
          <p className="ft-toggle">
            {isSignUp ? (
              <>
                Already have an account?{" "}
                <span className="ft-link" onClick={toggleMode}>Login here</span>
              </>
            ) : (
              <>
                Need to create an account?{" "}
                <span className="ft-link" onClick={toggleMode}>Sign up</span>
              </>
            )}
          </p>

          <div className="ft-footer-links">
            <span>Terms</span> | <span>Privacy Policy</span>
          </div>

        </div>
      </div>
    </>
  );
}